import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useCustomerAuth } from "../components/CustomerLayout";
import { 
  User,
  Camera,
  Save,
  LogOut,
  Trash2,
  Bell,
  BellOff,
  Loader2,
  ChevronRight,
  Shirt,
  Palette
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "XXL"];
const STYLE_OPTIONS = [
  { value: "casual", label: "Casual" },
  { value: "chic", label: "Chic" },
  { value: "sport", label: "Sport" },
  { value: "boheme", label: "Bohème" },
  { value: "classique", label: "Classique" },
];

const CATEGORY_OPTIONS = [
  "Robes",
  "Tops",
  "Pantalons",
  "Jupes",
  "Vestes",
  "Accessoires",
];

export default function CustomerProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { customer, getAuthHeaders, logout } = useCustomerAuth();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  
  // Form state
  const [firstName, setFirstName] = useState("");
  const [contact, setContact] = useState("");
  const [contactType, setContactType] = useState("email");
  const [preferredSize, setPreferredSize] = useState("");
  const [preferredCategories, setPreferredCategories] = useState([]);
  const [stylePreference, setStylePreference] = useState("");
  
  // Notification preferences
  const [notifNewItems, setNotifNewItems] = useState(true);
  const [notifFavorites, setNotifFavorites] = useState(true);
  const [notifPromotions, setNotifPromotions] = useState(true);
  const [notifMessages, setNotifMessages] = useState(true);
  const [notifOrders, setNotifOrders] = useState(true);

  useEffect(() => {
    if (customer) {
      setFirstName(customer.first_name || "");
      setContact(customer.contact || "");
      setContactType(customer.contact_type || "email");
      setPreferredSize(customer.preferred_size || "");
      setPreferredCategories(customer.preferred_categories || []);
      setStylePreference(customer.style_preference || "");
      setNotifNewItems(customer.notifications_new_items ?? true);
      setNotifFavorites(customer.notifications_favorites ?? true);
      setNotifPromotions(customer.notifications_promotions ?? true);
      setNotifMessages(customer.notifications_messages ?? true);
      setNotifOrders(customer.notifications_orders ?? true);
    }
    fetchCategories();
  }, [customer]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/public/vitrine/${userId}`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/customer/me`,
        {
          first_name: firstName,
          contact: contact,
          contact_type: contactType,
          preferred_size: preferredSize || null,
          preferred_categories: preferredCategories,
          style_preference: stylePreference || null,
          notifications_new_items: notifNewItems,
          notifications_favorites: notifFavorites,
          notifications_promotions: notifPromotions,
          notifications_messages: notifMessages,
          notifications_orders: notifOrders,
        },
        getAuthHeaders()
      );
      toast.success("Profil mis à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/api/customer/me`, getAuthHeaders());
      toast.success("Compte supprimé");
      logout();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
    setDeleteDialogOpen(false);
  };

  const toggleCategory = (category) => {
    if (preferredCategories.includes(category)) {
      setPreferredCategories(preferredCategories.filter(c => c !== category));
    } else {
      setPreferredCategories([...preferredCategories, category]);
    }
  };

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Profile Header */}
      <div className="text-center">
        <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="text-3xl font-bold text-[#D4AF37]">
            {firstName?.charAt(0)?.toUpperCase() || "?"}
          </span>
        </div>
        <h2 className="text-xl font-semibold text-[#1B1B1B]">{firstName}</h2>
        <p className="text-sm text-[#888888]">{contact}</p>
      </div>

      {/* Personal Info Section */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#F0F0F0]">
        <h3 className="font-medium text-[#1B1B1B] mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-[#D4AF37]" />
          Informations personnelles
        </h3>
        
        <div className="space-y-4">
          <div>
            <Label className="text-[#888888] text-sm">Prénom</Label>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="mt-1 bg-[#F5F5F5] border-0 rounded-xl"
            />
          </div>
          
          <div>
            <Label className="text-[#888888] text-sm">
              {contactType === "email" ? "Email" : "WhatsApp"}
            </Label>
            <Input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              className="mt-1 bg-[#F5F5F5] border-0 rounded-xl"
            />
          </div>
        </div>
      </div>

      {/* Style Preferences Section */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#F0F0F0]">
        <h3 className="font-medium text-[#1B1B1B] mb-4 flex items-center gap-2">
          <Shirt className="w-5 h-5 text-[#D4AF37]" />
          Mes préférences de style
        </h3>
        
        <div className="space-y-4">
          {/* Preferred Size */}
          <div>
            <Label className="text-[#888888] text-sm">Ma taille habituelle</Label>
            <Select value={preferredSize} onValueChange={setPreferredSize}>
              <SelectTrigger className="mt-1 bg-[#F5F5F5] border-0 rounded-xl">
                <SelectValue placeholder="Sélectionner une taille" />
              </SelectTrigger>
              <SelectContent>
                {SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={size}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Preferred Categories */}
          <div>
            <Label className="text-[#888888] text-sm mb-2 block">Mes catégories préférées</Label>
            <div className="flex flex-wrap gap-2">
              {(categories.length > 0 ? categories.map(c => c.name) : CATEGORY_OPTIONS).map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    preferredCategories.includes(cat)
                      ? "bg-[#D4AF37] text-white"
                      : "bg-[#F5F5F5] text-[#888888]"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          
          {/* Style Preference */}
          <div>
            <Label className="text-[#888888] text-sm">Mon style</Label>
            <Select value={stylePreference} onValueChange={setStylePreference}>
              <SelectTrigger className="mt-1 bg-[#F5F5F5] border-0 rounded-xl">
                <SelectValue placeholder="Sélectionner un style" />
              </SelectTrigger>
              <SelectContent>
                {STYLE_OPTIONS.map((style) => (
                  <SelectItem key={style.value} value={style.value}>
                    {style.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Notifications Section */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-[#F0F0F0]">
        <h3 className="font-medium text-[#1B1B1B] mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#D4AF37]" />
          Notifications
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#1B1B1B]">Nouveaux articles</p>
              <p className="text-xs text-[#888888]">Quand de nouveaux articles arrivent</p>
            </div>
            <Switch checked={notifNewItems} onCheckedChange={setNotifNewItems} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#1B1B1B]">Articles favoris</p>
              <p className="text-xs text-[#888888]">Disponibilité et baisses de prix</p>
            </div>
            <Switch checked={notifFavorites} onCheckedChange={setNotifFavorites} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#1B1B1B]">Promotions</p>
              <p className="text-xs text-[#888888]">Soldes et offres spéciales</p>
            </div>
            <Switch checked={notifPromotions} onCheckedChange={setNotifPromotions} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#1B1B1B]">Messages</p>
              <p className="text-xs text-[#888888]">Réponses de la boutique</p>
            </div>
            <Switch checked={notifMessages} onCheckedChange={setNotifMessages} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#1B1B1B]">Suivi commandes</p>
              <p className="text-xs text-[#888888]">Mises à jour de statut</p>
            </div>
            <Switch checked={notifOrders} onCheckedChange={setNotifOrders} />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-[#D4AF37] hover:bg-[#B8963C] text-white rounded-xl h-12"
      >
        {saving ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Save className="w-5 h-5 mr-2" />
            Enregistrer les modifications
          </>
        )}
      </Button>

      {/* Logout & Delete */}
      <div className="space-y-3 pt-4">
        <Button
          variant="outline"
          onClick={logout}
          className="w-full rounded-xl h-12 border-[#E5E5E5]"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Se déconnecter
        </Button>
        
        <Button
          variant="ghost"
          onClick={() => setDeleteDialogOpen(true)}
          className="w-full rounded-xl h-12 text-red-500 hover:text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-5 h-5 mr-2" />
          Supprimer mon compte
        </Button>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer mon compte ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Toutes vos données (favoris, historique de messages) seront supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-red-500 hover:bg-red-600"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
